import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Search, User } from 'lucide-react';
import { candidatesApi, jobConnectApi } from '../../services/api';

const terminalStatuses = ['FROZEN', 'CANCELED', 'SALE', 'DELETED', 'frozen', 'canceled', 'sold', 'deleted'];
const buttonClass = 'min-h-[44px] rounded-md px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500';

const candidateLabel = candidate => candidate.name
  || candidate.displayName
  || [candidate.firstname, candidate.lastname].filter(Boolean).join(' ')
  || 'Profil sans nom';

const JobConnectApplicationsInbox = ({ needId, needStatus, canMutate, onIdentifiedRefresh, onCountChange }) => {
  const [state, setState] = useState('loading');
  const [receipts, setReceipts] = useState([]);
  const [filter, setFilter] = useState('PENDING');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [acting, setActing] = useState(false);
  const [message, setMessage] = useState('');
  const [identityResolution, setIdentityResolution] = useState(null);
  const [identityQuery, setIdentityQuery] = useState('');
  const [identityCandidates, setIdentityCandidates] = useState([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState('');
  const [identityLoading, setIdentityLoading] = useState(false);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const response = await jobConnectApi.listApplicationReceipts(needId);
      const items = [...(response.items || [])]
        .sort((a, b) => new Date(b.receivedAt || 0) - new Date(a.receivedAt || 0));
      setReceipts(items);
      onCountChange?.(items.filter(item => item.reviewDecision === 'PENDING').length);
      setState(items.length ? 'ready' : 'empty');
    } catch (error) {
      setState('error');
    }
  }, [needId, onCountChange]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => receipts.filter(receipt => {
    if (receipt.reviewDecision !== filter) return false;
    const terms = `${receipt.candidate?.displayName || ''} ${receipt.candidate?.headline || ''}`.toLowerCase();
    return terms.includes(search.toLowerCase());
  }), [filter, receipts, search]);

  const decide = async decision => {
    if (!confirmation) return;
    setActing(true);
    setMessage('');
    try {
      await jobConnectApi.decideApplication(needId, confirmation.id, {
        expectedVersion: confirmation.version,
        decision,
      });
      setConfirmation(null);
      setSelected(null);
      await load();
      if (decision === 'ACCEPTED') await onIdentifiedRefresh?.();
      setMessage(decision === 'ACCEPTED' ? 'La candidature a rejoint Identifiés.' : 'La candidature a été rejetée.');
    } catch (error) {
      setMessage('La décision n’a pas pu être enregistrée. La candidature est conservée.');
    } finally {
      setActing(false);
    }
  };

  const openIdentityResolution = receipt => {
    setSelected(receipt);
    setIdentityResolution(receipt);
    setIdentityQuery(receipt.candidate?.displayName || '');
    setIdentityCandidates([]);
    setSelectedCandidateId('');
    setMessage('');
  };

  const searchIdentityCandidates = async () => {
    if (identityQuery.trim().length < 2) {
      setMessage('Saisissez au moins deux caractères pour rechercher un profil.');
      return;
    }
    setIdentityLoading(true);
    setMessage('');
    try {
      const response = await candidatesApi.listCandidates({
        search: identityQuery.trim(),
        limit: 20,
        offset: 0,
      });
      setIdentityCandidates(response.items || []);
    } catch (error) {
      setMessage('La recherche de profils est temporairement indisponible.');
    } finally {
      setIdentityLoading(false);
    }
  };

  const resolveIdentity = async () => {
    if (!identityResolution || !selectedCandidateId) return;
    setIdentityLoading(true);
    setMessage('');
    try {
      await jobConnectApi.resolveApplicationIdentity(needId, identityResolution.id, {
        expectedVersion: identityResolution.version,
        candidateId: selectedCandidateId,
      });
      setIdentityResolution(null);
      setSelected(null);
      await load();
      setMessage('Identité vérifiée. La candidature peut poursuivre son traitement.');
    } catch (error) {
      setMessage("L'identité n'a pas pu être vérifiée. La candidature est conservée.");
    } finally {
      setIdentityLoading(false);
    }
  };

  if (state === 'loading') {
    return <div aria-busy="true" className="flex min-h-[180px] items-center justify-center gap-2 text-gray-600"><Loader2 className="h-5 w-5 animate-spin" />Chargement des candidatures…</div>;
  }
  if (state === 'error') {
    return <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-5 text-red-800"><p className="font-semibold">Impossible de charger les candidatures reçues</p><button className={`${buttonClass} mt-3 border border-red-300 bg-white`} onClick={load}>Réessayer</button></div>;
  }
  if (state === 'empty') {
    return <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center"><User className="mx-auto h-10 w-10 text-gray-400" /><h3 className="mt-3 font-semibold">Aucune candidature reçue</h3><p className="mt-1 text-sm text-gray-600">Les candidatures JobConnect apparaîtront ici avant leur admission dans Identifiés.</p></div>;
  }

  const terminal = terminalStatuses.includes(needStatus);
  return (
    <section aria-labelledby="applications-title" className="space-y-4">
      <div><h3 id="applications-title" className="text-lg font-semibold">Candidatures reçues</h3><p className="text-sm text-gray-600">Les plus récentes en premier. Une validation Sales explicite est nécessaire.</p></div>
      {terminal && <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 font-medium text-amber-900">Besoin clôturé — validation impossible</p>}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div role="tablist" aria-label="Filtres des candidatures" className="flex flex-wrap gap-2">
          {[['PENDING', 'À traiter'], ['ACCEPTED', 'Validées'], ['REJECTED', 'Rejetées']].map(([value, label]) => (
            <button key={value} role="tab" aria-selected={filter === value} className={`${buttonClass} border ${filter === value ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-300 bg-white text-gray-700'}`} onClick={() => setFilter(value)}>{label}</button>
          ))}
        </div>
        <label className="relative text-sm font-medium"><span className="sr-only">Rechercher une candidature</span><Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-gray-400" /><input className="min-h-[44px] w-full rounded-md border-gray-300 pl-9 focus:border-blue-600 focus:ring-blue-600 sm:w-72" value={search} onChange={event => setSearch(event.target.value)} placeholder="Rechercher un nom ou un métier" /></label>
      </div>

      {message && <p role="alert" className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">{message}</p>}

      {confirmation && <div className="rounded-lg border border-amber-200 bg-amber-50 p-4"><h4 className="font-semibold">{confirmation.decision === 'ACCEPTED' ? `Ajouter ${confirmation.candidate?.displayName || 'ce profil'} à Identifiés ?` : `Rejeter la candidature de ${confirmation.candidate?.displayName || 'ce profil'} ?`}</h4><p className="mt-1 text-sm">{confirmation.decision === 'ACCEPTED' ? 'Le profil rejoindra le suivi candidat existant.' : 'Le rejet reste propre à cette candidature reçue.'}</p><div className="mt-3 flex gap-2"><button className={`${buttonClass} border border-gray-300 bg-white`} onClick={() => setConfirmation(null)}>Annuler</button><button className={`${buttonClass} ${confirmation.decision === 'ACCEPTED' ? 'bg-blue-600 text-white' : 'border border-red-300 bg-white text-red-700'}`} disabled={acting} onClick={() => decide(confirmation.decision)}>{confirmation.decision === 'ACCEPTED' ? 'Confirmer dans Identifiés' : 'Confirmer le rejet'}</button></div></div>}

      {identityResolution && (
        <section aria-labelledby="identity-title" className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <h4 id="identity-title" className="font-semibold">Vérifier l'identité de {identityResolution.candidate?.displayName || 'ce profil'}</h4>
          <p className="mt-1 text-sm text-gray-700">Associez cette candidature à un profil existant avant de poursuivre.</p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <label className="flex-1 text-sm font-medium">Rechercher un profil existant<input className="mt-1 min-h-[44px] w-full rounded-md border-gray-300 focus:border-blue-600 focus:ring-blue-600" value={identityQuery} onChange={event => setIdentityQuery(event.target.value)} /></label>
            <button className={`${buttonClass} self-end border border-amber-300 bg-white text-amber-900`} disabled={identityLoading} onClick={searchIdentityCandidates}>Rechercher</button>
          </div>
          {identityCandidates.length > 0 && <fieldset className="mt-3 space-y-2"><legend className="text-sm font-semibold">Profils correspondants</legend>{identityCandidates.map(candidate => <label key={candidate.id} className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-md border border-amber-200 bg-white px-3 py-2"><input type="radio" name="identity-candidate" value={candidate.id} checked={selectedCandidateId === candidate.id} onChange={event => setSelectedCandidateId(event.target.value)} /><span><strong>{candidateLabel(candidate)}</strong>{candidate.title && <span className="block text-sm text-gray-600">{candidate.title}</span>}</span></label>)}</fieldset>}
          <div className="mt-3 flex flex-wrap gap-2"><button className={`${buttonClass} border border-gray-300 bg-white`} onClick={() => setIdentityResolution(null)}>Annuler</button><button className={`${buttonClass} bg-blue-600 text-white`} disabled={!selectedCandidateId || identityLoading} onClick={resolveIdentity}>Confirmer l'identité</button></div>
        </section>
      )}

      {selected && <article className="rounded-lg border border-blue-200 bg-blue-50 p-4"><div className="flex items-start justify-between gap-3"><div><h4 className="text-lg font-semibold">Examiner {selected.candidate?.displayName || 'la candidature'}</h4><p className="text-sm text-gray-600">Cette candidature n’est pas encore dans Identifiés.</p></div><button className={`${buttonClass} border border-gray-300 bg-white`} onClick={() => { setSelected(null); setIdentityResolution(null); }}>Retour à la liste</button></div><dl className="mt-4 grid gap-3 md:grid-cols-2"><div><dt className="text-sm font-medium text-gray-600">Profil</dt><dd>{selected.candidate?.headline || 'Non renseigné'}</dd></div><div><dt className="text-sm font-medium text-gray-600">Analyse du CV</dt><dd>{selected.cvAnalysis?.status === 'READY' ? 'CV analysé' : 'Analyse du CV en cours'}</dd></div><div><dt className="text-sm font-medium text-gray-600">Score</dt><dd>{selected.score ? `${selected.score.value} / 100` : 'En cours de calcul'}</dd></div></dl></article>}

      <div className="grid gap-3">
        {visible.map(receipt => {
          const actions = receipt.availableActions || [];
          const ready = actions.includes('ACCEPT') && receipt.cvAnalysis?.status === 'READY' && !receipt.identityReviewRequired && !terminal;
          return (
            <article key={receipt.id} className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div><h4 className="font-semibold">{receipt.candidate?.displayName || 'Candidature à identifier'}</h4><p className="text-sm text-gray-600">{receipt.candidate?.headline || 'Profil JobConnect'} · {receipt.receivedAt ? new Date(receipt.receivedAt).toLocaleString('fr-FR') : 'Date non renseignée'}</p><div className="mt-2 flex flex-wrap gap-2 text-xs font-semibold"><span className={`rounded-full px-2 py-1 ${receipt.cvAnalysis?.status === 'READY' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-900'}`}>{receipt.cvAnalysis?.status === 'READY' ? 'CV analysé' : 'Analyse du CV en cours'}</span>{receipt.score && <span className="rounded-full bg-green-100 px-2 py-1 text-green-800">Score {receipt.score.value} / 100</span>}{receipt.identityReviewRequired && <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-900">Identité à vérifier</span>}</div></div>
                {canMutate && receipt.reviewDecision === 'PENDING' && <div className="flex flex-wrap gap-2"><button className={`${buttonClass} border border-gray-300 bg-white`} onClick={() => setSelected(receipt)}>Examiner</button>{actions.includes('RESOLVE_IDENTITY') && <button className={`${buttonClass} border border-amber-300 bg-white text-amber-900`} onClick={() => openIdentityResolution(receipt)}>Vérifier l'identité</button>}<button className={`${buttonClass} bg-blue-600 text-white`} disabled={!ready} onClick={() => setConfirmation({ ...receipt, decision: 'ACCEPTED' })}>Valider dans Identifiés</button><button className={`${buttonClass} border border-red-300 bg-white text-red-700`} onClick={() => setConfirmation({ ...receipt, decision: 'REJECTED' })}>Rejeter</button></div>}
              </div>
              {terminal && <p className="mt-3 text-sm font-medium text-amber-900">Besoin clôturé — validation impossible</p>}
              {receipt.processingState !== 'READY_FOR_REVIEW' && !receipt.identityReviewRequired && <p className="mt-3 text-sm text-gray-600">Analyse en cours. La validation sera disponible quand le profil sera prêt.</p>}
            </article>
          );
        })}
        {!visible.length && <p className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-gray-600">Aucune candidature dans ce filtre.</p>}
      </div>
    </section>
  );
};

export default JobConnectApplicationsInbox;
